"use client";

import { Calendar, ChevronLeft, Clock, Tag, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="11344" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="h-8 w-32 bg-muted/50 rounded-md animate-pulse mb-6" />
        <div className="h-12 w-3/4 bg-muted/50 rounded-md animate-pulse mb-4" />
        <div className="h-6 w-1/2 bg-muted/50 rounded-md animate-pulse mb-8" />
        <div className="aspect-[16/9] w-full bg-muted/50 rounded-lg animate-pulse mb-8" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-full bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-muted/50 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="text-2xl font-default font-semibold text-foreground mb-4">
          Article not found
        </h2>
        <p className="text-muted-foreground mb-6">
          {error || "The technical insight you are looking for is currently unavailable."}
        </p>
        <Link to="/HomePage">
          <Button variant="outline">
            <ChevronLeft className="h-4 w-4" />
            Return to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorName = post.author?.name || "Bedrock Engineering Team";
  const authorAvatar =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"] ||
    "";
  const authorInitials = authorName
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const readTime = post.content
    ? Math.max(1, Math.ceil(post.content.replace(/<[^>]+>/g, "").split(/\s+/).length / 200))
    : 5;

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      {}
      <div className="mb-8">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Overview
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {post.categories.map((cat, i) => (
            <span
              key={cat.id}
              data-index={i}
              className="inline-flex items-center px-3 py-1 text-xs font-medium font-mono uppercase tracking-wider bg-accent text-accent-foreground rounded-md"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default font-semibold text-foreground text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-6"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      {post.excerpt && (
        <div
          className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 font-default"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground mb-10">
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
            <AvatarFallback className="text-xs bg-primary text-primary-foreground">
              {authorInitials}
            </AvatarFallback>
          </Avatar>
          <span className="text-foreground font-medium">{authorName}</span>
        </div>
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formattedDate}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{readTime} min read</span>
        </div>
      </div>

      <Separator className="mb-10" />

      {}
      {post.featuredImage && (
        <div className="mb-10 md:mb-12 -mx-4 sm:-mx-6 lg:mx-0">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-lg shadow-lg"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose-content text-foreground leading-relaxed text-base md:text-lg space-y-5 [&>h2]:font-default [&>h2]:font-semibold [&>h2]:text-3xl [&>h2]:md:text-4xl [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:font-default [&>h3]:font-semibold [&>h3]:text-2xl [&>h3]:mt-10 [&>h3]:mb-3 [&>h3]:text-foreground [&>h4]:font-default [&>h4]:font-semibold [&>h4]:text-xl [&>h4]:mt-8 [&>h4]:mb-3 [&>h4]:text-foreground [&>p]:text-foreground [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:space-y-2 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:my-6 [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80 [&>img]:rounded-lg [&>img]:my-6 [&>img]:w-full [&>code]:font-mono [&>code]:text-sm [&>code]:bg-muted [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded [&>pre]:bg-muted [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            {post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="inline-flex items-center px-3 py-1 text-xs font-medium bg-secondary text-secondary-foreground rounded-md"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {}
      <Card className="mt-12 bg-card border border-border shadow-md">
        <CardContent className="px-6 py-6">
          <div className="flex items-center gap-2 mb-4 text-xs font-mono uppercase tracking-wider text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            Technical Lead
          </div>
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <Avatar className="h-16 w-16 shrink-0">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h4 className="font-default font-semibold text-lg text-foreground mb-1">
                {authorName}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Senior engineer at Bedrock Safety Engineering, specializing in fire protection systems and risk mitigation for high-hazard industrial environments.
              </p>
              {post.author?.link && (
                <Link
                  to={post.author.link}
                  className="inline-block mt-3 text-sm text-primary hover:underline underline-offset-4"
                >
                  View all technical insights
                </Link>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsConsumer({ currentPostId }: { currentPostId: string }) {
  const { posts, loading } = usePosts();

  const related = posts.filter((p) => p.id !== currentPostId).slice(0, 3);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-3">
            <div className="aspect-[4/3] w-full bg-muted/50 rounded-lg animate-pulse" />
            <div className="h-5 w-3/4 bg-muted/50 rounded animate-pulse" />
            <div className="h-4 w-1/2 bg-muted/50 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (related.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        No further technical articles available at this time.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
      {related.map((post, i) => {
        const formattedDate = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";

        return (
          <Link
            key={post.id}
            data-index={i}
            to={post.link || "#"}
            className="group block"
          >
            <Card className="h-full overflow-hidden bg-card border border-border hover:shadow-lg transition-shadow duration-300">
              {post.featuredImage ? (
                <div className="aspect-[4/3] w-full overflow-hidden">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] w-full bg-muted flex items-center justify-center">
                  <span className="font-default text-4xl text-muted-foreground/30">B</span>
                </div>
              )}
              <CardContent className="px-5 pt-5 pb-2">
                {post.categories && post.categories.length > 0 && (
                  <span className="inline-block text-xs font-mono uppercase tracking-wider text-primary mb-2">
                    {post.categories[0].name}
                  </span>
                )}
                <h3
                  className="font-default font-semibold text-lg md:text-xl text-foreground leading-snug mb-2 group-hover:text-primary transition-colors line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: post.title }}
                />
                {formattedDate && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{formattedDate}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

function RelatedPostsWrapper() {
  const { posts } = usePosts();
  const currentPostId = posts[0]?.id || "";
  const currentCategoryId = posts[0]?.categories?.[0]?.id;

  const relatedQueryParams: Record<string, any> = {
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc",
  };

  if (currentCategoryId) {
    relatedQueryParams.categories = [currentCategoryId];
  }

  const relatedQuery = new WP_Query(relatedQueryParams);

  return (
    <div className="bg-secondary/30 border-t border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="mb-10 md:mb-12 max-w-2xl">
          <span className="inline-block text-xs font-mono uppercase tracking-wider text-primary mb-3">
            Further Reading
          </span>
          <h2 className="font-default font-semibold text-3xl md:text-4xl text-foreground tracking-tight">
            Related Technical Insights
          </h2>
        </div>

        <PostsProvider wp_query={relatedQuery}>
          <RelatedPostsConsumer currentPostId={currentPostId} />
        </PostsProvider>
      </div>
    </div>
  );
}

export default function () {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      className="bg-background text-foreground w-full"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
        <RelatedPostsWrapper />
      </PostsProvider>
    </section>
  );
}